import { useLayoutEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useRoute } from "~router/navigation"
import { useStores, type Chat } from "~models"

type ChatStoreLookup = {
  byChatId?: (id: string) => Chat | undefined
  byId?: (id: number) => Chat | undefined
  chats?: { get: (id: string) => Chat | undefined }
  getChatByMessageRef?: (ref: {
    chat_cid?: string | null
    chat_id?: number | null
  }) => Chat | null | undefined
  current?: Chat | null
  setCurrent?: (chat: Chat) => void
}

/** 从 URL / 路由读取当前会话 cid（优先 searchParams，避免 history.state 残留） */
export function useRouteChatId(): string | undefined {
  const [searchParams] = useSearchParams()
  const route = useRoute<{ chat_id?: string; chat_cid?: string }>()
  const id =
    searchParams.get("chat_id") ??
    route.params?.chat_id ??
    route.params?.chat_cid
  return id != null && id !== "" ? String(id) : undefined
}

export function resolveChatById(
  chatStore: ChatStoreLookup,
  chatId?: string | number | null,
): Chat | null {
  if (chatId == null || chatId === "") return null
  const normalized = String(chatId)
  const numericId = /^\d+$/.test(normalized) ? Number(normalized) : null

  const byRef = chatStore.getChatByMessageRef?.({
    chat_cid: normalized,
    chat_id: numericId,
  })
  if (byRef) return byRef

  let target =
    chatStore.byChatId?.(normalized) || chatStore.chats?.get?.(normalized) || null
  if (!target && numericId != null) {
    target = chatStore.byId?.(numericId) ?? null
  }
  // 有些会话路由会携带短码/别名（chat.name），这里兜底按 name 解析
  if (!target && chatStore.chats?.get) {
    // chats 是 MST map，没有 values()；但上层 ChatStoreModel 暴露的是 map，这里只在缺省时做一次 O(n) 查找
    try {
      const anyStore = chatStore as any
      const values: Chat[] | undefined = anyStore?.chats?.values ? Array.from(anyStore.chats.values()) : undefined
      if (values?.length) {
        target = values.find((c) => String(c?.name ?? "") === normalized) ?? null
      }
    } catch {
      // ignore
    }
  }
  return target
}

/**
 * 以 URL 中的 chat_id 为唯一来源解析当前会话，并同步 chatStore.current。
 * 聊天页、消息列表必须使用此 hook，禁止直接读 chatStore.current。
 */
export function useActiveChat(): {
  chatId: string | undefined
  chat: Chat | null
} {
  const chatId = useRouteChatId()
  const { chatStore } = useStores()
  const chat = chatId ? resolveChatById(chatStore, chatId) : null

  useLayoutEffect(() => {
    if (!chat) return
    if (String(chatStore.current?.chat_id) !== String(chat.chat_id)) {
      chatStore.setCurrent(chat)
    }
  }, [chat, chatStore])

  return { chatId, chat }
}
